import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/JobOutcomes`;

/** completed | partial (the rest spills on) | notCompleted (runs again, next on the line) */
export type JobOutcomeKind = "completed" | "partial" | "notCompleted";

export interface JobOutcome {
	workOrderId: number;
	outcome: JobOutcomeKind;
	/** partial: minutes still to run */
	remainingMinutes?: number | null;
	note?: string | null;
	recordedBy?: string | null;
	recordedAt: string;
	/** Set once "Update schedule" has moved the line for it. */
	rescheduledAt?: string | null;
	moveBatchId?: string | null;
	continuationWorkOrderId?: number | null;
}

export interface JobOutcomes {
	outcomes: JobOutcome[];
	/** The order made for the rest of a partial job → the order it continues. */
	continuations: Record<string, number>;
}

export interface LineMove {
	/** 0 for the rest of a partial job (made on apply). */
	workOrderId: number;
	label: string;
	fromStart?: string | null;
	fromEnd?: string | null;
	toStart: string;
	toEnd: string;
	overtimeMinutes: number;
	kind: "continuation" | "rerun" | "pushed";
}

export interface RescheduleProposal {
	workOrderId: number;
	workstation: string;
	outcome: JobOutcomeKind;
	label: string;
	moves: LineMove[];
	manualPlacements: number;
	caughtUpAt?: string | null;
}

export interface RescheduleResult {
	moveBatchId: string;
	moved: number;
	continuationWorkOrderId?: number | null;
	from: string;
	to: string;
	/** The roster refill started for the days that changed. */
	fillJobId?: string | null;
}

export const jobOutcomesApi = {
	list: async (from: string, to: string): Promise<JobOutcomes> => {
		const response = await axios.get<JobOutcomes>(API_BASE_URL, { params: { from, to } });
		return { outcomes: response.data?.outcomes ?? [], continuations: response.data?.continuations ?? {} };
	},
	/** Record what happened; a null outcome clears it. Resolves to null when cleared. */
	record: async (workOrderId: number, outcome: JobOutcomeKind | null, remainingMinutes?: number | null, note?: string | null): Promise<JobOutcome | null> => {
		const response = await axios.put<JobOutcome>(`${API_BASE_URL}/${workOrderId}`, { outcome, remainingMinutes, note });
		return response.status === 204 ? null : response.data;
	},
	propose: async (workOrderId: number): Promise<RescheduleProposal> => {
		const response = await axios.get<RescheduleProposal>(`${API_BASE_URL}/${workOrderId}/reschedule`);
		return response.data;
	},
	apply: async (workOrderId: number): Promise<RescheduleResult> => {
		const response = await axios.post<RescheduleResult>(`${API_BASE_URL}/${workOrderId}/reschedule`);
		return response.data;
	},
	undo: async (moveBatchId: string): Promise<RescheduleResult> => {
		const response = await axios.post<RescheduleResult>(`${API_BASE_URL}/reschedule/${moveBatchId}/undo`);
		return response.data;
	},
};

export default jobOutcomesApi;
